<script lang="ts" setup>
import type { SlideshowConfig } from "@/models"
import { watchEffect } from "vue"
import { Button, Card } from "@/components/ui"
import { useTranslation } from "@/composables"

interface SlideshowTitleProps {
  slideshowConfig: SlideshowConfig
}

const props = defineProps<SlideshowTitleProps>()

const emit = defineEmits<{
  play: [slideshowConfig: SlideshowConfig]
}>()

const { t } = useTranslation()

watchEffect(() => {
  setTimeout(() => {
    emit("play", props.slideshowConfig)
  }, props.slideshowConfig.titleSlideDurationMs ?? 20000) // fallback of 20 seconds
})
</script>

<template>
  <div class="fixed inset-0 z-50 h-screen" data-test="slideshow-title-screen">
    <img
      v-if="slideshowConfig.eventVault?.coverImageUrl" class="scale-110 inset-0 bg-cover bg-center w-full object-cover fixed max-h-dvh h-full blur-lg brightness-75"
      :src="slideshowConfig.eventVault.coverImageUrl"
    >
    <div class="px-12 w-full h-full flex justify-center items-center">
      <Card variant="offwhite" class="md:px-6 z-10">
        <h1 v-if="slideshowConfig.eventVault?.title" class="text-xl md:text-3xl text-primary font-comfortaa text-center mb-3">
          {{ slideshowConfig.eventVault.title }}
        </h1>
        <p class="text-center mb-6 font-montserrat">
          {{ t('slideshow_title.description') }}
        </p>
        <div class="flex flex-col md:flex-row gap-5 md:gap-8 w-full">
          <div v-if="slideshowConfig.enableQrInTitleSlide && slideshowConfig.eventVault?.qrCodeUrl" class="w-full md:w-[400px] flex md:max-h-[400px]">
            <img :src="slideshowConfig.eventVault.qrCodeUrl" :alt="t('slideshow_title.qr_alt')" class="rounded-lg aspect-square">
          </div>
          <div v-if="slideshowConfig.eventVault?.coverImageUrl" class="w-full md:w-[400px] flex md:max-h-[400px]">
            <img :src="slideshowConfig.eventVault.coverImageUrl" :alt="t('slideshow_title.feature_alt')" class="aspect-square rounded-lg object-cover w-full">
          </div>
        </div>
        <div class="pt-6 md:pt-12 flex justify-center w-full">
          <Button prefix-icon="Play" size="lg" data-test="slideshow-title-play" @click="$emit('play', slideshowConfig)">
            {{ t("slideshow_title.play") }}
          </Button>
        </div>
      </Card>
    </div>
  </div>
</template>
